import { HowToAnswer } from "./HowToAnswer";
import { useState } from "react";

/**
 * Slider — one continuous slider between two labelled poles.
 *
 * Use ONLY for questions whose honest answer is genuinely "it depends".
 * This is the one place a midpoint is legitimate; everywhere else, force
 * a choice.
 *
 * Emits a value in 0..100 (integer steps of 5 for replay determinism).
 */

export interface SliderProps {
  leftLabel: string;
  rightLabel: string;
  /** Default position on first render. */
  initial?: number;
  onCommit: (value0to100: number) => void;
  ctaLabel?: string;
}

export function Slider({
  leftLabel, rightLabel, initial = 50, onCommit, ctaLabel = "Continue",
}: SliderProps) {
  const [value, setValue] = useState<number>(initial);
  const [touched, setTouched] = useState(false);

  return (
    <div className="space-y-8">
      <HowToAnswer>Drag the slider, or use the arrow keys, to show where you sit between the two ends.</HowToAnswer>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="text-muted-foreground">{leftLabel}</div>
        <div className="text-muted-foreground text-right">{rightLabel}</div>
      </div>

      <div className="relative">
        {/* Track fill — shows the lean at a glance, not a number. */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-3 rounded-full bg-secondary overflow-hidden pointer-events-none">
          <div
            className="h-full rounded-full bg-primary/70 transition-[width] duration-200 ease-out"
            style={{ width: `${value}%` }}
          />
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={value}
          onChange={(e) => { setValue(Number(e.target.value)); setTouched(true); }}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
          className="relative w-full h-3 bg-transparent accent-primary cursor-pointer
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4
            focus-visible:ring-offset-background rounded-full"
        />
      </div>

      <div className="text-center text-sm text-muted-foreground" aria-live="polite">
        {value === 50 ? "Right in the middle" : value < 50 ? "Leaning left" : "Leaning right"}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => onCommit(value)}
          disabled={!touched}
          className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
            transition-transform duration-150 active:scale-[0.98]
            disabled:opacity-40 disabled:pointer-events-none
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

